import React, { useEffect, useState } from "react";
import axios from "axios";
import backendUrl from "../../BackendUrl";

const TABS = ["Class", "Model", "Trim"];

const LeftPanel = ({
  selectedClass,
  setSelectedClass,
  selectedModel,
  setSelectedModel,
  selectedTrim,
  setSelectedTrim,
  hoveredModel,
  setHoveredModel,
  hoveredTrim,
  setHoveredTrim,
}) => {
  const [currentTab, setCurrentTab] = useState("Class");
  const [classes, setClasses] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch classes on mount
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${backendUrl}/api/vehicles/get-class`)
      .then((res) => setClasses(res.data.filter(item => item.ready)))
      .finally(() => setLoading(false));
  }, []);

  // Fetch models when class is selected
  useEffect(() => {
    if (!selectedClass) {
      setModels([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${backendUrl}/api/vehicles/get-model`, {
        params: { vehicleClassId: selectedClass._id },
      })
      .then((res) => {
        // Filter models to only those with the correct vehicleClassId and ready
        const filteredModels = res.data.filter(
          (model) =>
            model.vehicleClassId &&
            model.vehicleClassId.toString() === selectedClass._id.toString() &&
            model.ready
        );
        setModels(filteredModels);
      })
      .finally(() => setLoading(false));
  }, [selectedClass]);

  // Fetch trims when model is selected
  useEffect(() => {
    if (!selectedModel) {
      setTrims([]);
      return;
    }
    setLoading(true);
    axios
      .get(`${backendUrl}/api/vehicles/get-trim`, {
        params: { vehicleModelId: selectedModel._id },
      })
      .then((res) => {
        // Filter trims to only those with the correct vehicleModelId and ready
        const filteredTrims = res.data.filter(
          (trim) =>
            trim.vehicleModelId &&
            trim.vehicleModelId.toString() === selectedModel._id.toString() &&
            trim.ready
        );
        setTrims(filteredTrims);
      })
      .finally(() => setLoading(false));
  }, [selectedModel]);

  // Reset lower selections if upper changes
  useEffect(() => {
    setSelectedModel(null);
    setSelectedTrim(null);
    // eslint-disable-next-line
  }, [selectedClass]);
  useEffect(() => {
    setSelectedTrim(null);
    // eslint-disable-next-line
  }, [selectedModel]);

  // Tab navigation logic
  const canContinue =
    (currentTab === "Class" && selectedClass) ||
    (currentTab === "Model" && selectedModel) ||
    (currentTab === "Trim" && selectedTrim);

  const handleContinue = () => {
    if (currentTab === "Class" && selectedClass) setCurrentTab("Model");
    if (currentTab === "Model" && selectedModel) setCurrentTab("Trim");
  };

  // Renderers for each tab
  const renderClassTab = () => (
    <div className="selection-card">
      <h3>Select Class</h3>
      {loading && <div>Loading...</div>}
      {classes.map((cls) => (
        <button
          key={cls._id}
          className={`select-btn${selectedClass && selectedClass._id === cls._id ? " selected" : ""}`}
          onClick={() => setSelectedClass(cls)}
        >
          <div className="section-btn-title">{cls.class}</div>
          {cls.classImg && (
            <img src={cls.classImg} alt={cls.class} className="section-btn-img" />
          )}
          <div className="section-btn-desc">{cls.description}</div>
        </button>
      ))}
    </div>
  );

  const renderModelTab = () => (
    <div className="selection-card">
      <h3>Select Model</h3>
      {loading && <div>Loading...</div>}
      {models.map((model) => (
        <button
          key={model._id}
          className={`select-btn${selectedModel && selectedModel._id === model._id ? " selected" : ""}`}
          onClick={() => setSelectedModel(model)}
          onMouseEnter={() => setHoveredModel(model)}
          onMouseLeave={() => setHoveredModel(null)}
        >
          <div className="section-btn-title">{model.model}</div>
          {model.modelImg && (
            <img src={model.modelImg} alt={model.model} className="section-btn-img" />
          )}
        </button>
      ))}
    </div>
  );

  const renderTrimTab = () => (
    <div className="selection-card">
      <h3>Select Trim</h3>
      {loading && <div>Loading...</div>}
      {trims.map((trim) => (
        <button
          key={trim._id}
          className={`select-btn${selectedTrim && selectedTrim._id === trim._id ? " selected" : ""}`}
          onClick={() => setSelectedTrim(trim)}
          onMouseEnter={() => setHoveredTrim(trim)}
          onMouseLeave={() => setHoveredTrim(null)}
        >
          <div className="section-btn-title">{trim.trim}</div>
          {trim.trimImg && (
            <img src={trim.trimImg} alt={trim.trim} className="section-btn-img" />
          )}
        </button>
      ))}
    </div>
  );

  // CHANGE: Always show the selected class name under the tabs, regardless of tab
  const selectedName = selectedClass?.class;

  return (
    <aside className="left-panel">
      {/* Sticky tabs and select button */}
      <div className="left-panel-sticky">
        <div className="left-tabs">
          {TABS.map((tab) => (
            <span
              key={tab}
              className={currentTab === tab ? "active" : ""}
              onClick={() => {
                if (tab === "Class") {
                  setCurrentTab("Class");
                  setSelectedModel(null);
                  setSelectedTrim(null);
                } else if (tab === "Model" && selectedClass) {
                  setCurrentTab("Model");
                  setSelectedTrim(null);
                } else if (tab === "Trim" && selectedModel) {
                  setCurrentTab("Trim");
                }
              }}
              style={{
                cursor:
                  tab === currentTab ||
                  (tab === "Model" && !selectedClass) ||
                  (tab === "Trim" && !selectedModel)
                    ? "default"
                    : "pointer",
              }}
            >
              {tab}
            </span>
          ))}
        </div>
        {/* CHANGE: Always show class name as selected name */}
        {selectedName && (
          <div className="selected-name">
            <strong>{selectedName}</strong>
          </div>
        )}
        {currentTab !== "Trim" && (
          <button
            className="select-btn main-select-btn"
            onClick={handleContinue}
            disabled={!canContinue}
            style={{ width: "100%", margin: "12px 0" }}
          >
            Select {currentTab}
          </button>
        )}
      </div>
      {/* Scrollable area for options */}
      <div className="left-scrollable">
        {currentTab === "Class" && renderClassTab()}
        {currentTab === "Model" && renderModelTab()}
        {currentTab === "Trim" && renderTrimTab()}
      </div>
    </aside>
  );
};

export default LeftPanel;